import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { AuthService } from 'auth';
import { ENVIRONMENT, Logger, Platform, User } from 'shared';

/** A sibling site the user can hop to via one-click cross-login. */
export interface CrossLoginSite {
  key: string;
  name: string;
  url: string;
}

/**
 * Cross-site single sign-on between sibling sites that share the same backend
 * user base (registry: data/config/cross_login.json). The user mints a
 * short-lived, single-use handoff token on the current site and is redirected
 * to the target site, which exchanges it for a session — one click, no
 * password re-entry. See Api::SsoController.
 */
@Injectable({ providedIn: 'root' })
export class CrossLoginService {
  private readonly apiUrl =
    this.environment.apiUrl || this.environment.serverUrl || 'http://localhost:3000/api';
  private logger = new Logger('CrossLoginService');

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private platform: Platform,
    private inAppBrowser: InAppBrowser,
    @Inject(ENVIRONMENT) private environment: any
  ) {}

  /**
   * One-shot: mint a handoff token for `targetKey` and redirect the browser to
   * the target site. Use for a single dedicated button (the CrossLoginComponent
   * has its own per-site button handling).
   *
   * `fallbackUrl` is opened when the handoff cannot be minted (token endpoint
   * down, target not in the registry) — the user reaches the site anyway and
   * simply signs in there.
   *
   * `path` is the page to land on, relative to the language prefix
   * ("order-wizard"); without it the target opens its dashboard.
   */
  go(targetKey: string, fallbackUrl?: string, path?: string): void {
    this.handoffUrl(targetKey, path).subscribe({
      next: url => this.open(url),
      error: err => {
        this.logger.error('Cross-login handoff failed', err);
        // Without a fallback the button is simply dead when the handoff fails —
        // the user still wants to reach the site, just without being signed in.
        if (fallbackUrl) this.open(fallbackUrl);
      }
    });
  }

  private open(url: string): void {
    if (this.platform.is('hybrid')) {
      this.inAppBrowser.create(url, '_system');
    } else {
      window.location.href = url;
    }
  }

  /** Sibling sites the user can switch to (the backend excludes the current one). */
  targets(): Observable<CrossLoginSite[]> {
    return this.http
      .get<{ sites: CrossLoginSite[] }>(`${this.apiUrl}/sso/targets`)
      .pipe(map(r => r?.sites ?? []));
  }

  /** Mint a single-use handoff token for `targetKey`; resolves to the redirect URL. */
  handoffUrl(targetKey: string, path?: string): Observable<string> {
    const body: { target: string; path?: string } = { target: targetKey };
    if (path) body.path = path;
    return this.http
      .post<{ url: string }>(`${this.apiUrl}/sso/handoff`, body)
      .pipe(map(r => r.url));
  }

  /**
   * Exchange a handoff token (on the target site) for a session and establish
   * the authenticated state. Called by the /auth/handoff/:token page.
   */
  consume(token: string): Observable<User> {
    return this.http
      .post<{ success: boolean; user?: User; token?: string }>(
        `${this.apiUrl}/sso/handoff/consume`,
        { token }
      )
      .pipe(
        concatMap(res => {
          if (!res?.success || !res.user) {
            throw new Error('Cross-login failed');
          }
          return from(this.authService.setAuthState(res.user, res.token ?? null)).pipe(
            map(() => res.user as User)
          );
        })
      );
  }
}
